// Shared enums for 360 Kanbix. Values are stored in the database as-is, so never rename a value
// without a migration.

const values = <T extends Record<string, string>>(o: T) => Object.values(o) as [T[keyof T], ...T[keyof T][]];

/** Built-in issue type keys (PRD §3.1). Projects may add more types on top of these. */
export const IssueTypeKey = {
  EPIC: 'epic',
  STORY: 'story',
  TASK: 'task',
  BUG: 'bug',
  SUBTASK: 'subtask',
  QUICK_TASK: 'quick_task',
} as const;
export type IssueTypeKey = (typeof IssueTypeKey)[keyof typeof IssueTypeKey];
export const ISSUE_TYPE_KEYS = values(IssueTypeKey);

/** Hierarchy levels: 1 = Epic, 2 = Story/Task/Bug/Quick Task, 3 = Sub-task. */
export const HierarchyLevel = { EPIC: 1, STANDARD: 2, SUBTASK: 3 } as const;
export type HierarchyLevel = 1 | 2 | 3;

export const StatusCategory = {
  TRIAGE: 'triage',
  TODO: 'todo',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
} as const;
export type StatusCategory = (typeof StatusCategory)[keyof typeof StatusCategory];
export const STATUS_CATEGORIES = values(StatusCategory);

/** Well-known status keys used by the default workflows (FR-WF-2, FR-WF-3) and routing rules. */
export const StatusKey = {
  TRIAGE: 'triage',
  TODO: 'todo',
  IN_PROGRESS: 'in_progress',
  IN_CODE_REVIEW: 'in_code_review',
  READY_FOR_QA: 'ready_for_qa',
  REOPENED: 'reopened',
  AWAITING_APPROVAL: 'awaiting_approval',
  DONE: 'done',
} as const;
export type StatusKey = (typeof StatusKey)[keyof typeof StatusKey];

export const Priority = {
  HIGHEST: 'highest',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  LOWEST: 'lowest',
} as const;
export type Priority = (typeof Priority)[keyof typeof Priority];
export const PRIORITIES = values(Priority);

export const Severity = {
  CRITICAL: 'critical',
  MAJOR: 'major',
  MINOR: 'minor',
  TRIVIAL: 'trivial',
} as const;
export type Severity = (typeof Severity)[keyof typeof Severity];
export const SEVERITIES = values(Severity);

/** Resolution lookup categories (FR-PRJ-3). Resolutions themselves are per-project rows. */
export const ResolutionCategory = { DONE: 'done', WONT_DO: 'wont_do' } as const;
export type ResolutionCategory = (typeof ResolutionCategory)[keyof typeof ResolutionCategory];
export const RESOLUTION_CATEGORIES = values(ResolutionCategory);

/** Resolutions seeded into every new project. */
export const DEFAULT_RESOLUTIONS: { name: string; category: ResolutionCategory }[] = [
  { name: 'Done', category: 'done' },
  { name: 'Fixed', category: 'done' },
  { name: "Won't fix", category: 'wont_do' },
  { name: 'Duplicate', category: 'wont_do' },
  { name: 'Cannot reproduce', category: 'wont_do' },
];

/** Rank contexts (§7.3): backlog order and sprint order are independent. */
export const RankContext = { BACKLOG: 'backlog', SPRINT: 'sprint' } as const;
export type RankContext = (typeof RankContext)[keyof typeof RankContext];

/** Link types (FR-WI-6). Each stored link has a forward type; the inverse is derived for display. */
export const LinkType = {
  BLOCKS: 'blocks',
  RELATES_TO: 'relates_to',
  DUPLICATES: 'duplicates',
  CLONES: 'clones',
} as const;
export type LinkType = (typeof LinkType)[keyof typeof LinkType];
export const LINK_TYPES = values(LinkType);
export const LINK_LABELS: Record<LinkType, { outward: string; inward: string }> = {
  blocks: { outward: 'blocks', inward: 'is blocked by' },
  relates_to: { outward: 'relates to', inward: 'relates to' },
  duplicates: { outward: 'duplicates', inward: 'is duplicated by' },
  clones: { outward: 'clones', inward: 'is cloned by' },
};

/** Role owner fields on a work item (FR-ASG-1). */
export const ItemRole = {
  DEV_OWNER: 'dev_owner',
  QA_OWNER: 'qa_owner',
  REVIEWER: 'reviewer',
  APPROVER: 'approver',
} as const;
export type ItemRole = (typeof ItemRole)[keyof typeof ItemRole];
export const ITEM_ROLES = values(ItemRole);

export const OrgRole = { ORG_ADMIN: 'org_admin', MEMBER: 'member' } as const;
export type OrgRole = (typeof OrgRole)[keyof typeof OrgRole];
export const ORG_ROLES = values(OrgRole);

/** Project-level roles (PRD §2). Release Manager / QA Manager are stored on the release itself. */
export const ProjectRole = {
  PROJECT_ADMIN: 'project_admin',
  PRODUCT_OWNER: 'product_owner',
  DEVELOPER: 'developer',
  QA_ENGINEER: 'qa_engineer',
  REPORTER: 'reporter',
  VIEWER: 'viewer',
} as const;
export type ProjectRole = (typeof ProjectRole)[keyof typeof ProjectRole];
export const PROJECT_ROLES = values(ProjectRole);

export const ReleaseType = { MAJOR: 'major', MINOR: 'minor', PATCH: 'patch' } as const;
export type ReleaseType = (typeof ReleaseType)[keyof typeof ReleaseType];
export const RELEASE_TYPES = values(ReleaseType);

export const ReleaseStatus = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  RELEASED: 'released',
  ARCHIVED: 'archived',
} as const;
export type ReleaseStatus = (typeof ReleaseStatus)[keyof typeof ReleaseStatus];
export const RELEASE_STATUSES = values(ReleaseStatus);

/** Build states (FR-REL-3). */
export const BuildState = {
  BUILDING: 'building',
  READY_FOR_QA: 'ready_for_qa',
  IN_QA: 'in_qa',
  REJECTED: 'rejected',
  SUPERSEDED: 'superseded',
  PROMOTED: 'promoted',
} as const;
export type BuildState = (typeof BuildState)[keyof typeof BuildState];
export const BUILD_STATES = values(BuildState);

/** A promoted build keeps its type; promotion is a state, not a new build (CORRECTIONS #7). */
export const BuildType = { BETA: 'beta', PATCH: 'patch' } as const;
export type BuildType = (typeof BuildType)[keyof typeof BuildType];
export const BUILD_TYPES = values(BuildType);

export const BuildChangeType = { FEATURE: 'feature', FIX: 'fix', CARRIED: 'carried' } as const;
export type BuildChangeType = (typeof BuildChangeType)[keyof typeof BuildChangeType];
export const BUILD_CHANGE_TYPES = values(BuildChangeType);

export const SprintState = { FUTURE: 'future', ACTIVE: 'active', CLOSED: 'closed' } as const;
export type SprintState = (typeof SprintState)[keyof typeof SprintState];

export const BoardType = { SCRUM: 'scrum', KANBAN: 'kanban', TRIAGE: 'triage' } as const;
export type BoardType = (typeof BoardType)[keyof typeof BoardType];
export const BOARD_TYPES = values(BoardType);

/** Activity types for worklogs (FR-TIME-7). */
export const ActivityType = {
  DEVELOPMENT: 'development',
  CODE_REVIEW: 'code_review',
  QA_TESTING: 'qa_testing',
  DEPLOYMENT: 'deployment',
  MEETING: 'meeting',
  DOCUMENTATION: 'documentation',
  SUPPORT: 'support',
  PRE_SALES: 'pre_sales',
} as const;
export type ActivityType = (typeof ActivityType)[keyof typeof ActivityType];
export const ACTIVITY_TYPES = values(ActivityType);

export const WorklogSource = { MANUAL: 'manual', TIMER: 'timer', SESSION: 'session', CHAT: 'chat' } as const;
export type WorklogSource = (typeof WorklogSource)[keyof typeof WorklogSource];

/** Remaining estimate handling when logging time (FR-TIME-3). */
export const RemainingMode = { AUTO: 'auto', SET: 'set', LEAVE: 'leave' } as const;
export type RemainingMode = (typeof RemainingMode)[keyof typeof RemainingMode];

/** Quick task categories (FR-QT-2). */
export const QuickTaskCategory = {
  SUPPORT: 'support',
  PRE_SALES: 'pre_sales',
  INTERNAL: 'internal',
  ADMIN: 'admin',
} as const;
export type QuickTaskCategory = (typeof QuickTaskCategory)[keyof typeof QuickTaskCategory];
export const QUICK_TASK_CATEGORIES = values(QuickTaskCategory);

export const CustomFieldType = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  SELECT: 'select',
  MULTI_SELECT: 'multi_select',
  USER: 'user',
  URL: 'url',
} as const;
export type CustomFieldType = (typeof CustomFieldType)[keyof typeof CustomFieldType];
export const CUSTOM_FIELD_TYPES = values(CustomFieldType);

export const NoteAssignmentStatus = {
  PENDING: 'pending',
  ACKNOWLEDGED: 'acknowledged',
  DONE: 'done',
} as const;
export type NoteAssignmentStatus = (typeof NoteAssignmentStatus)[keyof typeof NoteAssignmentStatus];
export const NOTE_ASSIGNMENT_STATUSES = values(NoteAssignmentStatus);

/** Document types (FR-DOC-4). */
export const DocType = {
  BRD: 'BRD',
  PRD: 'PRD',
  SRS: 'SRS',
  USER_GUIDE: 'USER_GUIDE',
  CONFIG_GUIDE: 'CONFIG_GUIDE',
  RELEASE_NOTES: 'RELEASE_NOTES',
  TEST_STRATEGY: 'TEST_STRATEGY',
  RUNBOOK: 'RUNBOOK',
  MEETING_NOTES: 'MEETING_NOTES',
  MEETING_RECORDING: 'MEETING_RECORDING',
  DEMO_VIDEO: 'DEMO_VIDEO',
  OTHER: 'OTHER',
} as const;
export type DocType = (typeof DocType)[keyof typeof DocType];
export const DOC_TYPES = values(DocType);

export const DocStatus = {
  DRAFT: 'draft',
  IN_REVIEW: 'in_review',
  APPROVED: 'approved',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
} as const;
export type DocStatus = (typeof DocStatus)[keyof typeof DocStatus];
export const DOC_STATUSES = values(DocStatus);

export const ScanStatus = { PENDING: 'pending', CLEAN: 'clean', INFECTED: 'infected' } as const;
export type ScanStatus = (typeof ScanStatus)[keyof typeof ScanStatus];

export const DocProcessingStatus = {
  PENDING: 'pending',
  EXTRACTING: 'extracting',
  INDEXED: 'indexed',
  FAILED: 'failed',
} as const;
export type DocProcessingStatus = (typeof DocProcessingStatus)[keyof typeof DocProcessingStatus];

export const MediaOwnerType = {
  DOCUMENT_VERSION: 'document_version',
  WORK_ITEM: 'work_item',
  COMMENT: 'comment',
  TEST_RESULT: 'test_result',
  NOTE: 'note',
  MESSAGE: 'message',
} as const;
export type MediaOwnerType = (typeof MediaOwnerType)[keyof typeof MediaOwnerType];
export const MEDIA_OWNER_TYPES = values(MediaOwnerType);
/** Owners enabled in Phase 1; test_result and message ship with their modules in Phase 2 (FR-MED-3). */
export const PHASE1_MEDIA_OWNER_TYPES: MediaOwnerType[] = ['document_version', 'work_item', 'comment', 'note'];
/** Owners enabled as of Phase 2 (test results; messages follow with channels). */
export const ENABLED_MEDIA_OWNER_TYPES: MediaOwnerType[] = ['document_version', 'work_item', 'comment', 'note', 'test_result', 'message'];

export const MediaKind = { VIDEO: 'video', AUDIO: 'audio' } as const;
export type MediaKind = (typeof MediaKind)[keyof typeof MediaKind];

export const MediaSource = {
  UPLOAD: 'upload',
  SCREEN_RECORDING: 'screen_recording',
  CAMERA: 'camera',
  VOICE_NOTE: 'voice_note',
} as const;
export type MediaSource = (typeof MediaSource)[keyof typeof MediaSource];
export const MEDIA_SOURCES = values(MediaSource);

export const MediaProcessingStatus = {
  PENDING: 'pending',
  PROBING: 'probing',
  TRANSCODING: 'transcoding',
  READY: 'ready',
  FAILED: 'failed',
} as const;
export type MediaProcessingStatus = (typeof MediaProcessingStatus)[keyof typeof MediaProcessingStatus];

export const AttachmentOwnerType = {
  WORK_ITEM: 'work_item',
  COMMENT: 'comment',
  NOTE: 'note',
} as const;
export type AttachmentOwnerType = (typeof AttachmentOwnerType)[keyof typeof AttachmentOwnerType];
export const ATTACHMENT_OWNER_TYPES = values(AttachmentOwnerType);

/** Notification types (FR-NTF-2). */
export const NotificationType = {
  ASSIGNED: 'assigned',
  MENTIONED: 'mentioned',
  HANDOFF: 'handoff',
  APPROVAL_REQUEST: 'approval_request',
  TRIAGE_DEV: 'triage_dev',
  TRIAGE_QA: 'triage_qa',
  NOTE_ASSIGNED: 'note_assigned',
  NOTE_ASSIGNMENT_UPDATED: 'note_assignment_updated',
  REMINDER: 'reminder',
  WATCHED_CHANGE: 'watched_change',
  COMMENT: 'comment',
  BUILD_READY: 'build_ready',
  DOCUMENT_READY: 'document_ready',
  MEDIA_READY: 'media_ready',
  TIMESHEET_SUBMITTED: 'timesheet_submitted',
  TIMESHEET_DECIDED: 'timesheet_decided',
  TIMESHEET_REMINDER: 'timesheet_reminder',
  TRIAGE_AGEING: 'triage_ageing',
  DEPUTY_ACTIVE: 'deputy_active',
  APPROVAL_DECIDED: 'approval_decided',
  AUTOMATION: 'automation',
  DIRECT_MESSAGE: 'direct_message',
  ANNOUNCEMENT: 'announcement',
  DIGEST: 'digest',
  /** An AI agent (MCP) proposed a change that waits for your confirmation. */
  AI_PROPOSAL: 'ai_proposal',
  SPRINT_RISK: 'sprint_risk',
  /** Critical bug for an important customer (FR-INT-6); the only type that may be sent by SMS. */
  CRITICAL_ESCALATION: 'critical_escalation',
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];
export const NOTIFICATION_TYPES = values(NotificationType);

export const NotificationChannel = { IN_APP: 'in_app', PUSH: 'push', EMAIL: 'email', SMS: 'sms' } as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];
export const NOTIFICATION_CHANNELS = values(NotificationChannel);

export const AiProposalStatus = {
  PROPOSED: 'proposed',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
} as const;
export type AiProposalStatus = (typeof AiProposalStatus)[keyof typeof AiProposalStatus];

/** AI features that can be toggled per org/project (AI-P7). */
export const AiFeature = {
  STORY_DRAFT: 'story_draft',
  BUG_DRAFT: 'bug_draft',
  TASK_DRAFT: 'task_draft',
  DUPLICATES: 'duplicates',
  NOTE_ACTIONS: 'note_actions',
  NOTE_SUMMARY: 'note_summary',
  NL_SEARCH: 'nl_search',
  ASSISTANT: 'assistant',
  DOC_SUMMARY: 'doc_summary',
  DOC_CLASSIFY: 'doc_classify',
  REQUIREMENT_EXTRACT: 'requirement_extract',
  DOC_QA: 'doc_qa',
  DOC_GAPS: 'doc_gaps',
  DOC_IMPACT: 'doc_impact',
  GUIDE_DRAFT: 'guide_draft',
  OCR: 'ocr',
  TRANSCRIPTION: 'transcription',
  MEETING_SUMMARY: 'meeting_summary',
  TEST_CASE_GEN: 'test_case_gen',
  FAILURE_CLUSTER: 'failure_cluster',
  RETEST_SCOPE: 'retest_scope',
  RELEASE_NARRATIVE: 'release_narrative',
  QUALITY_CHECK: 'quality_check',
  TRIAGE_SUGGEST: 'triage_suggest',
  ESTIMATE_SUGGEST: 'estimate_suggest',
  LINK_SUGGEST: 'link_suggest',
  TIME_PARSE: 'time_parse',
  CATCH_UP: 'catch_up',
  STANDUP_DRAFT: 'standup_draft',
  COMMENT_ASSIST: 'comment_assist',
  SEMANTIC_SEARCH: 'semantic_search',
  ASK_DATA: 'ask_data',
  SPRINT_PLANNING: 'sprint_planning',
  FLAKY_TESTS: 'flaky_tests',
} as const;
export type AiFeature = (typeof AiFeature)[keyof typeof AiFeature];
export const AI_FEATURES = values(AiFeature);

// ---------------------------------------------------------------------------------------------------
// Test management (Phase 2, PRD §4.8)
// ---------------------------------------------------------------------------------------------------

/** Result statuses. "Retest" is not a status — `needs_retest` is a flag on a result (CORRECTIONS). */
export const TestResultStatus = {
  NOT_RUN: 'not_run',
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  SKIPPED: 'skipped',
} as const;
export type TestResultStatus = (typeof TestResultStatus)[keyof typeof TestResultStatus];
export const TEST_RESULT_STATUSES = values(TestResultStatus);

export const SuiteType = {
  SMOKE: 'smoke',
  BETA: 'beta',
  PATCH: 'patch',
  REGRESSION: 'regression',
  UPGRADE: 'upgrade',
  SECURITY: 'security',
} as const;
export type SuiteType = (typeof SuiteType)[keyof typeof SuiteType];
export const SUITE_TYPES = values(SuiteType);

export const TestCaseType = { MANUAL: 'manual', AUTOMATED: 'automated' } as const;
export type TestCaseType = (typeof TestCaseType)[keyof typeof TestCaseType];
export const TEST_CASE_TYPES = values(TestCaseType);

export const TestRunKind = { INITIAL: 'initial', RETEST: 'retest' } as const;
export type TestRunKind = (typeof TestRunKind)[keyof typeof TestRunKind];

export const TestRunStatus = { NOT_STARTED: 'not_started', IN_PROGRESS: 'in_progress', COMPLETED: 'completed' } as const;
export type TestRunStatus = (typeof TestRunStatus)[keyof typeof TestRunStatus];

export const DefectLinkType = { CAUSED_BY: 'caused_by', KNOWN_ISSUE: 'known_issue' } as const;
export type DefectLinkType = (typeof DefectLinkType)[keyof typeof DefectLinkType];

export const MandatoryEvidence = { NONE: 'none', FAILED: 'failed', CRITICAL: 'critical', ALL: 'all' } as const;
export type MandatoryEvidence = (typeof MandatoryEvidence)[keyof typeof MandatoryEvidence];
export const MANDATORY_EVIDENCE = values(MandatoryEvidence);

export const Readiness = { READY: 'ready', AT_RISK: 'at_risk', IN_PROGRESS: 'in_progress' } as const;
export type Readiness = (typeof Readiness)[keyof typeof Readiness];

/** Timesheet approval cycle (FR-TIME-10): Draft → Submitted → Approved/Rejected; approved weeks are locked. */
export const TimesheetStatus = { DRAFT: 'draft', SUBMITTED: 'submitted', APPROVED: 'approved', REJECTED: 'rejected' } as const;
export type TimesheetStatus = (typeof TimesheetStatus)[keyof typeof TimesheetStatus];
export const TIMESHEET_STATUSES = values(TimesheetStatus);

/** Approval steps (FR-WF-7): permanent records, never deleted. */
export const ApprovalStatus = { PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected', CANCELLED: 'cancelled' } as const;
export type ApprovalStatus = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];
export const ApprovalKind = { TRANSITION: 'transition', GO_NO_GO: 'go_no_go' } as const;
export type ApprovalKind = (typeof ApprovalKind)[keyof typeof ApprovalKind];

export const RETRO_COLUMNS = ['went_well', 'improve', 'action'] as const;
export type RetroColumn = (typeof RETRO_COLUMNS)[number];

export const CHANNEL_TYPES = ['team', 'qa', 'announcements', 'release', 'custom', 'dm', 'group'] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];
export const PAGE_TEMPLATES = ['blank', 'release_notes', 'test_strategy', 'runbook', 'decision'] as const;
export type PageTemplate = (typeof PAGE_TEMPLATES)[number];
